import { Building2, Check, ChevronDown, Mail, Phone, Plus, Search, Trash2, User, X } from "lucide-react"
import { useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  useGetBrandingCompaniesQuery,
  useGetBrandingSettingsQuery,
  useUpdateBrandingSettingsMutation,
} from "../../../../../app/services/book/saved_tours_branding"

const emptyState = {
  header: { enabled: false, logo_url: "", show_company_name: true, company_name_override: "" },
  manager: { enabled: false, show_name: true, show_phone: true, show_email: true },
  footer: { enabled: false, blocks: [] },
}

const uid = () => Math.random().toString(36).slice(2, 10)

const Toggle = ({ checked, onChange, label }) => (
  <label className="flex items-center gap-2.5 cursor-pointer select-none shrink-0">
    <span
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors shrink-0
                  ${checked ? "bg-[#1E9E5B]" : "bg-[#D8DEE6]"}`}
    >
      <span
        className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform
                    ${checked ? "translate-x-[18px]" : "translate-x-[3px]"}`}
      />
    </span>
    {label && <span className="text-sm text-[#26303D]">{label}</span>}
  </label>
)

const SectionCard = ({ title, description, enabled, onToggleEnabled, children }) => (
  <div className="border border-[#E7EAEF] rounded-xl p-3 sm:p-4">
    <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold text-[#1F2733]">{title}</div>
        {description && <div className="text-xs text-[#9AA5B4] mt-0.5">{description}</div>}
      </div>
      <Toggle checked={enabled} onChange={onToggleEnabled} label="" />
    </div>
    {enabled && <div className="pt-2 border-t border-[#F0F2F5] flex flex-col gap-3">{children}</div>}
  </div>
)

const TextField = ({ label, value, onChange, placeholder }) => (
  <div>
    <div className="text-xs text-[#5A6779] mb-1">{label}</div>
    <input
      type="text"
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full text-sm border border-[#E7EAEF] rounded-lg px-3 py-2 focus:outline-none focus:border-[#9AA5B4]"
    />
  </div>
)

// Инициалы для аватарки-заглушки компании в списке
const initials = (name) =>
  (name || "")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase()

const CompanyCombobox = ({ companies, value, onChange, myCompanyLabel }) => {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState("")
  const [highlighted, setHighlighted] = useState(0)
  const rootRef = useRef(null)
  const inputRef = useRef(null)
  const listRef = useRef(null)

  const selectedCompany = useMemo(
    () => companies.find((c) => c.id === value) || null,
    [companies, value]
  )

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return companies
    return companies.filter((c) => c.name.toLowerCase().includes(q))
  }, [companies, query])

  useEffect(() => {
    if (!open) return
    const handleClickOutside = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false)
        setQuery("")
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [open])

  useEffect(() => {
    if (open) {
      setHighlighted(0)
      requestAnimationFrame(() => inputRef.current?.focus())
    }
  }, [open])

  useEffect(() => {
    setHighlighted(0)
  }, [query])

  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-idx="${highlighted}"]`)
    el?.scrollIntoView({ block: "nearest" })
  }, [highlighted])

  const selectAt = (idx) => {
    // idx 0 = "Моя компания" (null), затем filtered[idx - 1]
    if (idx === 0) {
      onChange(null)
    } else {
      const company = filtered[idx - 1]
      if (company) onChange(company.id)
    }
    setOpen(false)
    setQuery("")
  }

  const handleKeyDown = (e) => {
    const optionsCount = filtered.length + 1 // +1 за "Моя компания"
    if (e.key === "ArrowDown") {
      e.preventDefault()
      setHighlighted((h) => Math.min(h + 1, optionsCount - 1))
    } else if (e.key === "ArrowUp") {
      e.preventDefault()
      setHighlighted((h) => Math.max(h - 1, 0))
    } else if (e.key === "Enter") {
      e.preventDefault()
      selectAt(highlighted)
    } else if (e.key === "Escape") {
      e.preventDefault()
      setOpen(false)
      setQuery("")
    }
  }

  return (
    <div ref={rootRef} className="relative w-full sm:w-80">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`w-full flex items-center gap-2.5 rounded-lg border px-3 py-2.5 text-left transition-colors
                    ${open ? "border-[#26303D] bg-white" : "border-[#E7EAEF] bg-[#F4F6F9] hover:bg-[#EEF1F5]"}`}
      >
        <span className="flex items-center justify-center h-6 w-6 rounded-md bg-[#26303D] text-white text-[10px] font-semibold shrink-0">
          {selectedCompany ? initials(selectedCompany.name) : <Building2 size={13} />}
        </span>
        <span className="flex-1 min-w-0 text-sm text-[#26303D] truncate">
          {selectedCompany ? selectedCompany.name : myCompanyLabel}
        </span>
        {selectedCompany && (
          <span
            onClick={(e) => {
              e.stopPropagation()
              onChange(null)
            }}
            className="p-1 rounded-md text-[#9AA5B4] hover:text-[#26303D] hover:bg-[#EEF1F5] shrink-0"
            title={myCompanyLabel}
          >
            <X size={13} />
          </span>
        )}
        <ChevronDown
          size={15}
          className={`text-[#9AA5B4] shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open && (
        <div className="absolute z-20 mt-1.5 w-full bg-white border border-[#E7EAEF] rounded-xl shadow-lg overflow-hidden">
          <div className="flex items-center gap-2 border-b border-[#F0F2F5] px-3 py-2">
            <Search size={14} className="text-[#9AA5B4] shrink-0" />
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Поиск компании..."
              className="w-full text-sm focus:outline-none placeholder:text-[#B7C0CC]"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery("")}
                className="text-[#9AA5B4] hover:text-[#26303D] shrink-0"
              >
                <X size={13} />
              </button>
            )}
          </div>

          <div ref={listRef} className="max-h-72 overflow-y-auto py-1">
            <div
              data-idx={0}
              onMouseEnter={() => setHighlighted(0)}
              onClick={() => selectAt(0)}
              className={`flex items-center gap-2.5 px-3 py-2 cursor-pointer text-sm
                          ${highlighted === 0 ? "bg-[#F4F6F9]" : ""}`}
            >
              <span className="flex items-center justify-center h-5 w-5 rounded-md bg-[#EEF1F5] text-[#5A6779] shrink-0">
                <Building2 size={11} />
              </span>
              <span className="flex-1 min-w-0 truncate text-[#26303D] font-medium">{myCompanyLabel}</span>
              {value === null && <Check size={14} className="text-[#1E9E5B] shrink-0" />}
            </div>

            {filtered.length > 0 && <div className="h-px bg-[#F0F2F5] my-1 mx-3" />}

            {filtered.length === 0 && query && (
              <div className="px-3 py-6 text-center text-xs text-[#9AA5B4]">Ничего не найдено</div>
            )}

            {filtered.map((c, i) => {
              const idx = i + 1
              const isSelected = value === c.id
              return (
                <div
                  key={c.id}
                  data-idx={idx}
                  onMouseEnter={() => setHighlighted(idx)}
                  onClick={() => selectAt(idx)}
                  className={`flex items-center gap-2.5 px-3 py-2 cursor-pointer text-sm
                              ${highlighted === idx ? "bg-[#F4F6F9]" : ""}`}
                >
                  <span className="flex items-center justify-center h-5 w-5 rounded-md bg-[#EEF1F5] text-[#5A6779] text-[9px] font-semibold shrink-0">
                    {initials(c.name)}
                  </span>
                  <span className={`flex-1 min-w-0 truncate ${isSelected ? "text-[#1F2733] font-medium" : "text-[#26303D]"}`}>
                    {c.name}
                  </span>
                  {isSelected && <Check size={14} className="text-[#1E9E5B] shrink-0" />}
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

const BrandingTab = ({ isAdmin }) => {
  const { t } = useTranslation()
  const [companyId, setCompanyId] = useState(null)

  const { data: companiesData } = useGetBrandingCompaniesQuery(undefined, { skip: !isAdmin })
  const { data, isLoading } = useGetBrandingSettingsQuery(companyId)
  const [updateBrandingSettings, { isLoading: isSaving }] = useUpdateBrandingSettingsMutation()

  const [state, setState] = useState(emptyState)
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    if (data?.settings) {
      setState({
        header: { ...emptyState.header, ...data.settings.header },
        manager: { ...emptyState.manager, ...data.settings.manager },
        footer: { ...emptyState.footer, ...data.settings.footer, blocks: data.settings.footer?.blocks || [] },
      })
    } else {
      setState(emptyState)
    }
  }, [data])

  const patchSection = (section, patch) =>
    setState((prev) => ({ ...prev, [section]: { ...prev[section], ...patch } }))

  const addFooterBlock = (type) =>
    patchSection("footer", {
      blocks: [
        ...state.footer.blocks,
        type === "text" ? { id: uid(), type: "text", text: "" } : { id: uid(), type: "link", label: "", url: "" },
      ],
    })

  const updateFooterBlock = (id, patch) =>
    patchSection("footer", {
      blocks: state.footer.blocks.map((b) => (b.id === id ? { ...b, ...patch } : b)),
    })

  const removeFooterBlock = (id) =>
    patchSection("footer", { blocks: state.footer.blocks.filter((b) => b.id !== id) })

  const handleSave = async () => {
    await updateBrandingSettings({
      companyId,
      header_enabled: state.header.enabled,
      header_logo_url: state.header.logo_url,
      header_show_company_name: state.header.show_company_name,
      header_company_name_override: state.header.company_name_override,
      manager_enabled: state.manager.enabled,
      manager_show_name: state.manager.show_name,
      manager_show_phone: state.manager.show_phone,
      manager_show_email: state.manager.show_email,
      footer_enabled: state.footer.enabled,
      footer_blocks: state.footer.blocks,
    })
    setSaved(true)
    setTimeout(() => setSaved(false), 1800)
  }

  if (isLoading) {
    return <div className="text-sm text-[#9AA5B4] py-10 text-center">{t("common.loading", "Загрузка...")}</div>
  }

  return (
    <div className="flex flex-col gap-4 sm:gap-5">
      <div className="text-sm text-[#5A6779]">
        {t(
          "saved_tours.branding.description",
          "Настройте блоки, которые будут показываться на странице подборки: хедер, блок менеджера и футер. Настройки применяются ко всем подборкам компании."
        )}
      </div>

      {isAdmin && companiesData?.companies?.length > 0 && (
        <div>
          <div className="text-xs text-[#5A6779] mb-1.5">
            {t("saved_tours.branding.company_scope", "Настройки для компании")}
          </div>
          <CompanyCombobox
            companies={companiesData.companies}
            value={companyId}
            onChange={setCompanyId}
            myCompanyLabel={t("saved_tours.branding.my_company", "Моя компания")}
          />
        </div>
      )}

      <SectionCard
        title={t("saved_tours.branding.header_title", "Хедер")}
        description={t("saved_tours.branding.header_description", "Логотип и название компании над подборкой")}
        enabled={state.header.enabled}
        onToggleEnabled={(v) => patchSection("header", { enabled: v })}
      >
        <TextField
          label={t("saved_tours.branding.logo_url", "Ссылка на логотип")}
          value={state.header.logo_url}
          onChange={(v) => patchSection("header", { logo_url: v })}
          placeholder="https://..."
        />
        <Toggle
          checked={state.header.show_company_name}
          onChange={(v) => patchSection("header", { show_company_name: v })}
          label={t("saved_tours.branding.show_company_name", "Показывать название компании")}
        />
        {state.header.show_company_name && (
          <TextField
            label={t("saved_tours.branding.company_name_override", "Название компании (необязательно, иначе берётся из профиля)")}
            value={state.header.company_name_override}
            onChange={(v) => patchSection("header", { company_name_override: v })}
          />
        )}
        {(state.header.logo_url || (state.header.show_company_name && state.header.company_name_override)) && (
          <div className="flex flex-wrap items-center gap-3 bg-[#F4F6F9] rounded-lg px-3 sm:px-4 py-3">
            {state.header.logo_url && (
              <img src={state.header.logo_url} alt="" className="h-8 w-auto max-w-[120px] object-contain shrink-0" />
            )}
            {state.header.show_company_name && (
              <span className="text-sm font-semibold text-[#1F2733] break-words min-w-0">
                {state.header.company_name_override || t("saved_tours.branding.company_name_placeholder", "Название компании")}
              </span>
            )}
          </div>
        )}
      </SectionCard>

      <SectionCard
        title={t("saved_tours.branding.manager_title", "Блок менеджера")}
        description={t(
          "saved_tours.branding.manager_description",
          "Имя, телефон и почта показываются автоматически по менеджеру, создавшему подборку — здесь выбираете, какие поля видны"
        )}
        enabled={state.manager.enabled}
        onToggleEnabled={(v) => patchSection("manager", { enabled: v })}
      >
        <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2.5 sm:gap-4">
          <Toggle
            checked={state.manager.show_name}
            onChange={(v) => patchSection("manager", { show_name: v })}
            label={t("saved_tours.branding.show_name", "Имя")}
          />
          <Toggle
            checked={state.manager.show_phone}
            onChange={(v) => patchSection("manager", { show_phone: v })}
            label={t("saved_tours.branding.show_phone", "Телефон")}
          />
          <Toggle
            checked={state.manager.show_email}
            onChange={(v) => patchSection("manager", { show_email: v })}
            label={t("saved_tours.branding.show_email", "Почта")}
          />
        </div>
        <div className="flex flex-wrap items-center gap-x-4 sm:gap-x-6 gap-y-1.5 text-sm text-[#26303D] bg-[#F4F6F9] rounded-lg px-3 sm:px-4 py-3">
          {state.manager.show_name && (
            <span className="flex items-center gap-1.5">
              <User size={14} className="text-[#9AA5B4]" /> {t("saved_tours.branding.manager_example_name", "Иван Иванов")}
            </span>
          )}
          {state.manager.show_phone && (
            <span className="flex items-center gap-1.5">
              <Phone size={14} className="text-[#9AA5B4]" /> +373 000 000
            </span>
          )}
          {state.manager.show_email && (
            <span className="flex items-center gap-1.5">
              <Mail size={14} className="text-[#9AA5B4]" /> manager@example.com
            </span>
          )}
        </div>
      </SectionCard>

      <SectionCard
        title={t("saved_tours.branding.footer_title", "Футер")}
        description={t("saved_tours.branding.footer_description", "Произвольные блоки текста или ссылок внизу страницы")}
        enabled={state.footer.enabled}
        onToggleEnabled={(v) => patchSection("footer", { enabled: v })}
      >
        {state.footer.blocks.map((block) => (
          <div key={block.id} className="flex items-start gap-2 bg-[#F4F6F9] rounded-lg p-3">
            <div className="flex-1 min-w-0 flex flex-col gap-2">
              {block.type === "text" ? (
                <textarea
                  value={block.text}
                  onChange={(e) => updateFooterBlock(block.id, { text: e.target.value })}
                  placeholder={t("saved_tours.branding.footer_text_placeholder", "Текст блока")}
                  rows={2}
                  className="w-full text-sm border border-[#E7EAEF] rounded-lg px-3 py-2 resize-none focus:outline-none focus:border-[#9AA5B4]"
                />
              ) : (
                <>
                  <input
                    type="text"
                    value={block.label}
                    onChange={(e) => updateFooterBlock(block.id, { label: e.target.value })}
                    placeholder={t("saved_tours.branding.footer_link_label_placeholder", "Текст ссылки")}
                    className="w-full text-sm border border-[#E7EAEF] rounded-lg px-3 py-2 focus:outline-none focus:border-[#9AA5B4]"
                  />
                  <input
                    type="text"
                    value={block.url}
                    onChange={(e) => updateFooterBlock(block.id, { url: e.target.value })}
                    placeholder="https://..."
                    className="w-full text-sm border border-[#E7EAEF] rounded-lg px-3 py-2 focus:outline-none focus:border-[#9AA5B4]"
                  />
                </>
              )}
            </div>
            <button
              onClick={() => removeFooterBlock(block.id)}
              className="p-2 rounded-lg text-[#9AA5B4] hover:text-red-500 hover:bg-white transition-colors shrink-0"
            >
              <Trash2 size={15} />
            </button>
          </div>
        ))}

        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => addFooterBlock("text")}
            className="flex items-center gap-1.5 text-xs text-[#5A6779] border border-[#E7EAEF] rounded-lg px-3 py-1.5 bg-white hover:bg-[#F4F6F9]"
          >
            <Plus size={13} /> {t("saved_tours.branding.add_text_block", "Текстовый блок")}
          </button>
          <button
            onClick={() => addFooterBlock("link")}
            className="flex items-center gap-1.5 text-xs text-[#5A6779] border border-[#E7EAEF] rounded-lg px-3 py-1.5 bg-white hover:bg-[#F4F6F9]"
          >
            <Plus size={13} /> {t("saved_tours.branding.add_link_block", "Ссылка")}
          </button>
        </div>
      </SectionCard>

      <div className="flex items-center gap-3 flex-wrap">
        <button
          onClick={handleSave}
          disabled={isSaving}
          className="text-sm text-white bg-[#26303D] rounded-lg px-4 py-2 disabled:opacity-60 w-full sm:w-auto"
        >
          {isSaving ? t("common.saving", "Сохранение...") : t("common.save", "Сохранить")}
        </button>
        {saved && <span className="text-sm text-[#1E9E5B]">{t("common.saved", "Сохранено")}</span>}
      </div>
    </div>
  )
}

export default BrandingTab
